import {
  EmailAuthProvider,
  MultiFactorResolver,
  NextOrObserver,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword
} from 'firebase/auth'
import {authAuditApi, contactsApi} from 'lib/api'
import {ERRORS_CODE_MESSAGES_MAP, FirebaseInstances} from './helpers'
import {setBrowserPushPrincipal} from 'lib/push/browserPush'

const DEFAULT_LOCAL_EMAIL_ALLOWLIST = ['yopmail.com']
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1'])
const PROTECTED_OUTBOUND_EMAIL_ENVIRONMENTS = new Set([
  'development',
  'staging',
  'test',
  'local',
  'preview'
])
const OUTBOUND_EMAIL_BLOCKED_MESSAGE =
  'Outbound email is restricted to allowlisted domains by the current safe outbound email policy.'

const parseBooleanEnv = (value?: string | null) =>
  `${value || ''}`.trim().toLowerCase() === 'true'

const getDeclaredAppEnvironment = () =>
  `${
    process.env.REACT_APP_PUBLIC_APPLICATION_ENVIRONMENT ||
    process.env.VERCEL_ENV ||
    ''
  }`
    .trim()
    .toLowerCase()

const getAllowedEmailDomains = () => {
  const configured = `${process.env.REACT_APP_EMAIL_ALLOWLIST_DOMAINS || ''}`
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)

  return configured.length ? configured : DEFAULT_LOCAL_EMAIL_ALLOWLIST
}

const getRecipientDomain = (email: string) =>
  `${email || ''}`.trim().toLowerCase().split('@').slice(1).join('@')

const isAllowedRecipientDomain = (domain: string, allowedDomains: string[]) =>
  allowedDomains.some(
    (allowedDomain) =>
      domain === allowedDomain || domain.endsWith(`.${allowedDomain}`)
  )

const isSafeOutboundEmailRuntime = () => {
  if (parseBooleanEnv(process.env.REACT_APP_FORCE_SAFE_OUTBOUND_EMAILS)) {
    return true
  }

  if (parseBooleanEnv(process.env.REACT_APP_ALLOW_UNSAFE_OUTBOUND_EMAILS)) {
    return false
  }

  if (typeof window === 'undefined') {
    return false
  }

  if (PROTECTED_OUTBOUND_EMAIL_ENVIRONMENTS.has(getDeclaredAppEnvironment())) {
    return true
  }

  return LOCAL_HOSTS.has(window.location.hostname.toLowerCase())
}

const guardClientOutboundEmail = (email: string) => {
  if (!isSafeOutboundEmailRuntime()) {
    return null
  }

  const domain = getRecipientDomain(email)
  const allowedDomains = getAllowedEmailDomains()

  if (domain && isAllowedRecipientDomain(domain, allowedDomains)) {
    return null
  }

  return {
    code: 'auth/outbound-email-blocked',
    message: OUTBOUND_EMAIL_BLOCKED_MESSAGE
  }
}

const getAuthPagePath = () =>
  typeof window === 'undefined'
    ? ''
    : `${window.location.pathname}${window.location.search}`

const getAccountClaims = (claims: unknown): Array<string> => {
  if (!claims || typeof claims !== 'object' || Array.isArray(claims)) {
    return []
  }

  const accounts = (claims as Record<string, unknown>).accounts
  return Array.isArray(accounts)
    ? accounts.filter(
        (account): account is string =>
          typeof account === 'string' && account.trim().length > 0
      )
    : []
}

const hasTotpEnrollment = (factors: Array<{factorId?: string}>) =>
  factors.some(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID
  )

export type AdminClaimsResult = {
  doesCustomClaimsExist: boolean
  isSuperAdmin: boolean
  isMSAAdmin: boolean
  isSuperManager: boolean
  mfaEnrolled: boolean
  /**
   * The claims could not be read at all — a failed ID token refresh, not an
   * answer about this account. Every privilege flag stays false, so a caller
   * that only asks "is this an admin?" still denies; a caller that can tell the
   * difference can offer a retry instead of ending the session.
   */
  verificationUnavailable?: boolean
  error?: unknown
}

export const recordAdminAuthAuditEvent = (params: {
  email: string
  outcome: 'succeeded' | 'failed' | 'denied'
  errorCode?: string
  reason?: string
}) => {
  const email = `${params.email || ''}`.trim().toLowerCase()
  if (!email) return

  void authAuditApi
    .record({
      email,
      outcome: params.outcome,
      surface: 'admin',
      errorCode: params.errorCode,
      reason: params.reason,
      pagePath: getAuthPagePath()
    })
    .catch(() => {})
}

export function createAuthService(fb: FirebaseInstances) {
  let pendingMfaResolver: MultiFactorResolver | null = null
  let pendingTotpSecret: TotpSecret | null = null

  let observedPrincipalUid: string | null | undefined
  const sessionObserver = (callback: NextOrObserver<User | null>) =>
    fb.auth.onAuthStateChanged((user) => {
      const nextUid = user?.uid || null
      if (observedPrincipalUid !== nextUid) {
        pendingMfaResolver = null
        pendingTotpSecret = null
        observedPrincipalUid = nextUid
      }

      void setBrowserPushPrincipal(nextUid).catch(() => {})

      if (typeof callback === 'function') {
        callback(user)
      } else {
        callback.next?.(user)
      }
    })

  const signIn = async (email: string, password: string) => {
    try {
      const {user} = await signInWithEmailAndPassword(fb.auth, email, password)
      return {
        success: true,
        uid: user.uid,
        user
      }
    } catch (error: any) {
      const code = error.code
      if (code === 'auth/multi-factor-auth-required') {
        pendingMfaResolver = getMultiFactorResolver(fb.auth, error)
        const supportsTotp = pendingMfaResolver.hints.some(
          (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
        )
        if (supportsTotp) {
          return {mfaRequired: true}
        }
      }
      recordAdminAuthAuditEvent({
        email,
        outcome: 'failed',
        errorCode: code,
        reason: 'firebase_auth_error'
      })
      return {
        error: {
          code,
          message: ERRORS_CODE_MESSAGES_MAP[code]
        }
      }
    }
  }

  const resolveMfaSignIn = async (oneTimePassword: string) => {
    const resolver = pendingMfaResolver
    const code = `${oneTimePassword || ''}`.replace(/\s/g, '')
    if (!resolver) {
      return {
        error: {
          code: 'auth/multi-factor-session-missing',
          message: 'Start the sign-in process again.'
        }
      }
    }
    const hint = resolver.hints.find(
      (entry) => entry.factorId === TotpMultiFactorGenerator.FACTOR_ID
    )
    if (!hint) {
      return {
        error: {
          code: 'auth/unsupported-second-factor',
          message:
            'This account does not have a supported authenticator factor.'
        }
      }
    }
    if (!/^\d{6}$/.test(code)) {
      return {
        error: {
          code: 'auth/invalid-verification-code',
          message: 'Enter the current 6-digit authenticator code.'
        }
      }
    }

    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        hint.uid,
        code
      )
      const credential = await resolver.resolveSignIn(assertion)
      pendingMfaResolver = null
      return {
        success: true,
        uid: credential.user.uid,
        user: credential.user
      }
    } catch (error: any) {
      return {
        error: {
          code: error?.code || 'auth/invalid-verification-code',
          message:
            ERRORS_CODE_MESSAGES_MAP[error?.code] ||
            'The authenticator code could not be verified.'
        }
      }
    }
  }

  const reauthenticateAdmin = async (password: string) => {
    const currentUser = fb.auth.currentUser
    if (!currentUser?.email) {
      return {
        error: {
          code: 'auth/user-not-found',
          message: 'Sign in again before performing this action.'
        }
      }
    }
    if (!password) {
      return {
        error: {
          code: 'auth/missing-password',
          message: 'Enter your current password.'
        }
      }
    }

    pendingMfaResolver = null
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        password
      )
      const authenticated = await reauthenticateWithCredential(
        currentUser,
        credential
      )
      const factors = multiFactor(authenticated.user).enrolledFactors
      if (!hasTotpEnrollment(factors)) {
        return {
          error: {
            code: 'auth/mfa-enrollment-required',
            message:
              'Set up an authenticator before performing this sensitive action.'
          }
        }
      }
      await authenticated.user.getIdToken(true)
      return {success: true, user: authenticated.user}
    } catch (error: any) {
      if (error?.code === 'auth/multi-factor-auth-required') {
        pendingMfaResolver = getMultiFactorResolver(fb.auth, error)
        const supportsTotp = pendingMfaResolver.hints.some(
          (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
        )
        if (supportsTotp) {
          return {mfaRequired: true}
        }
        pendingMfaResolver = null
      }

      return {
        error: {
          code: error?.code || 'auth/reauthentication-failed',
          message:
            ERRORS_CODE_MESSAGES_MAP[error?.code] ||
            error?.message ||
            'Your identity could not be verified.'
        }
      }
    }
  }

  const completeAdminReauthentication = async (oneTimePassword: string) => {
    const result = await resolveMfaSignIn(oneTimePassword)
    if (result.success && result.user) {
      await result.user.getIdToken(true)
    }
    return result
  }

  const getMfaStatus = async () => {
    const currentUser = fb.auth.currentUser
    if (!currentUser) {
      return {
        enrolled: false,
        factors: [] as Array<string>,
        emailVerified: false,
        email: ''
      }
    }
    await currentUser.reload()
    const factors = multiFactor(currentUser).enrolledFactors
    return {
      enrolled: hasTotpEnrollment(factors),
      factors: factors.map((factor) => factor.factorId),
      emailVerified: currentUser.emailVerified,
      email: currentUser.email || ''
    }
  }

  const beginTotpEnrollment = async () => {
    const currentUser = fb.auth.currentUser
    if (!currentUser?.email) {
      throw new Error(
        'Sign in again before setting up multi-factor authentication.'
      )
    }
    if (!currentUser.emailVerified) {
      throw new Error(
        'Verify your admin email address before setting up two-step verification.'
      )
    }
    const status = await getMfaStatus()
    if (status.enrolled) {
      return {alreadyEnrolled: true}
    }
    const session = await multiFactor(currentUser).getSession()
    pendingTotpSecret = await TotpMultiFactorGenerator.generateSecret(session)
    const accountName = currentUser.email
    const issuer = 'DADE Auctions Admin'

    return {
      alreadyEnrolled: false,
      secretKey: pendingTotpSecret.secretKey,
      accountName,
      issuer,
      qrCodeUrl: pendingTotpSecret.generateQrCodeUrl(accountName, issuer),
      enrollmentCompletionDeadline:
        pendingTotpSecret.enrollmentCompletionDeadline
    }
  }

  const completeTotpEnrollment = async (oneTimePassword: string) => {
    const currentUser = fb.auth.currentUser
    const code = `${oneTimePassword || ''}`.replace(/\s/g, '')
    if (!currentUser || !pendingTotpSecret) {
      throw new Error('Start authenticator setup again.')
    }
    if (!/^\d{6}$/.test(code)) {
      throw new Error('Enter the current 6-digit authenticator code.')
    }
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      pendingTotpSecret,
      code
    )
    await multiFactor(currentUser).enroll(assertion, 'DADE Admin authenticator')
    pendingTotpSecret = null
    await currentUser.getIdToken(true)
    return {success: true}
  }

  const doSignOut = () => firebaseSignOut(fb.auth)

  const resetPassword = async (email: string) => {
    try {
      const blockedError = guardClientOutboundEmail(email)
      if (blockedError) {
        return {
          error: blockedError
        }
      }
      await contactsApi.sendPasswordResetLink({email, audience: 'admin'})
      return {
        success: true
      }
    } catch (error: any) {
      const code = error.code
      return {
        error: {
          code,
          message:
            ERRORS_CODE_MESSAGES_MAP[code] ||
            error?.message ||
            'Password reset email could not be sent.'
        }
      }
    }
  }

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    const currentUser = fb.auth?.currentUser
    try {
      if (!currentUser)
        throw new Error('User not logged in.', {
          cause: {
            message: 'User not logged in.',
            name: 'USER_NOT_FOUND '
          }
        })

      const authCredentials = EmailAuthProvider.credential(
        currentUser.email || '',
        currentPassword
      )

      const authenticated = await reauthenticateWithCredential(
        currentUser,
        authCredentials
      )
      if (authenticated) {
        await updatePassword(currentUser, newPassword)
        return {message: 'Password has been updated.'}
      } else {
        throw new Error(`Incorrect current password`, {
          cause: {
            message: 'Incorrect current password.',
            name: 'INCORRECT_CURRENT_PASS '
          }
        })
      }
    } catch (error: any) {
      return {error: error.message}
    }
  }

  const checkCustomClaims = async (
    authenticatedUser?: User
  ): Promise<AdminClaimsResult> => {
    try {
      const currentUser = authenticatedUser ?? fb.auth.currentUser
      const idTokenResult: any = currentUser
        ? await currentUser.getIdTokenResult(true)
        : {}
      const userClaimsObject =
        idTokenResult?.claims &&
        typeof idTokenResult.claims === 'object' &&
        !Array.isArray(idTokenResult.claims)
          ? (idTokenResult.claims as Record<string, unknown>)
          : {}
      const userAccountsList = getAccountClaims(userClaimsObject)
      const isSuperAdmin = userClaimsObject.superAdmin === true
      const isSuperManager = userClaimsObject.superManager === true
      const isMSAAdmin = userAccountsList.length > 0
      const doesCustomClaimsExist: boolean =
        isSuperAdmin || isSuperManager || isMSAAdmin
      const mfaEnrolled = currentUser
        ? hasTotpEnrollment(multiFactor(currentUser).enrolledFactors)
        : false
      return {
        doesCustomClaimsExist,
        isSuperAdmin,
        isMSAAdmin,
        isSuperManager,
        mfaEnrolled
      }
    } catch (error) {
      // The forced refresh itself failed — the token endpoint was unreachable,
      // App Check could not vouch for the request, the network dropped. Nothing
      // was verified in either direction, so no privilege is granted here; the
      // caller is told the verification was unavailable rather than being left
      // to read an absent answer as a removed admin role.
      return {
        doesCustomClaimsExist: false,
        isSuperAdmin: false,
        isMSAAdmin: false,
        isSuperManager: false,
        mfaEnrolled: false,
        verificationUnavailable: true,
        error
      }
    }
  }

  const getUserAccounts = async () => {
    const idTokenResult: any = fb.auth.currentUser
      ? await fb.auth.currentUser.getIdTokenResult(true)
      : {}
    const userClaimsObject = idTokenResult?.claims || {}
    return getAccountClaims(userClaimsObject)
  }

  return {
    sessionObserver,
    signIn,
    resolveMfaSignIn,
    reauthenticateAdmin,
    completeAdminReauthentication,
    getMfaStatus,
    beginTotpEnrollment,
    completeTotpEnrollment,
    signOut: doSignOut,
    resetPassword,
    changePassword,
    checkCustomClaims,
    getUserAccounts
  }
}
